import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CompactFilterBar, Input, Badge, Modal, Select, LoadingSpinner, Pagination } from '@/components/common';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '@/services/api';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store';
import { canManageVehicleMaster } from './masterDataAccess';
import { BulkActionBar, BulkSelectAllCheckbox, BulkSelectCheckbox, useBulkSelection } from '@/components/bulk';
import type { PaginatedResponse } from '@/types';

interface FuelType {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
}

// Status is a plain is_active boolean -- '' means "don't filter", so the
// value that goes on the wire is only ever 'true' or 'false' once chosen.
const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const initialForm = { name: '', code: '' };

const itemsPerPage = 20;

export function FuelTypesPage() {
  const queryClient = useQueryClient();
  // Read-only for everyone but the country admin -- which is exactly
  // where the server draws the line. See ./masterDataAccess.ts.
  const canManage = canManageVehicleMaster(useAuthStore().user?.role);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [filterStatus, setFilterStatus] = useState('');
  const [formData, setFormData] = useState(initialForm);

  const { data, isLoading, isError, error, refetch } = useQuery<PaginatedResponse<FuelType>>({
    queryKey: ['fuel-types', page, debouncedSearch, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(itemsPerPage) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterStatus) params.set('is_active', filterStatus);
      const res = await api.get(`/vehicles/fuel-types/?${params.toString()}`);
      return res.data;
    },
  });
  const fuelTypes = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const visibleIds = fuelTypes.map((f) => f.id);
  const selection = useBulkSelection(visibleIds);
  const activeFilterCount = filterStatus ? 1 : 0;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['fuel-types'] });
    queryClient.invalidateQueries({ queryKey: ['vehicle-fuel-types-lookup'] });
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof initialForm) => {
      if (editingId) {
        const res = await api.patch(`/vehicles/fuel-types/${editingId}/`, data);
        return res.data;
      }
      const res = await api.post('/vehicles/fuel-types/', data);
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success(editingId ? 'Fuel type updated' : 'Fuel type created');
      setShowModal(false);
      setEditingId(null);
      setFormData(initialForm);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/vehicles/fuel-types/${id}/`);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Fuel type deleted');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handleEdit = (ft: FuelType) => {
    setEditingId(ft.id);
    setFormData({ name: ft.name, code: ft.code });
    setShowModal(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this fuel type? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialForm);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Fuel Types</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage vehicle fuel types</p>
        </div>
        {canManage && (
          <Button variant="primary" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setShowModal(true)}>
            Add Fuel Type
          </Button>
        )}
      </div>

      <CompactFilterBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder="Search fuel types..."
        activeFilterCount={activeFilterCount}
        onClearFilters={() => {
          setFilterStatus('');
          setPage(1);
        }}
      >
        <Select
          id="fuel-type-filter-status"
          label="Status"
          options={statusOptions}
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
        />
      </CompactFilterBar>

      {canManage && (
        <BulkActionBar
          resource="vehicles/fuel-types"
          entityName="fuel types"
          selectedIds={selection.selectedIds}
          onChanged={invalidate}
          onClear={selection.clear}
        />
      )}

      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          // A failed request is not "there are no fuel types" -- those are
          // two different states and this platform has a habit of confusing
          // them.
          <div role="alert" className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900">Could not load fuel types</h3>
            <p className="mt-2 text-gray-500">{getErrorMessage(error)}</p>
            <Button className="mt-4" variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : fuelTypes.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No fuel types found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Get started by adding a fuel type.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {canManage && (
                      <th className="px-6 py-3 text-left">
                        <BulkSelectAllCheckbox
                          checked={selection.allVisibleSelected}
                          indeterminate={selection.someVisibleSelected}
                          onChange={selection.toggleAllVisible}
                        />
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {fuelTypes.map((ft) => (
                    <tr key={ft.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      {canManage && (
                        <td className="px-6 py-4">
                          <BulkSelectCheckbox
                            checked={selection.isSelected(ft.id)}
                            onChange={() => selection.toggle(ft.id)}
                            label={`Select ${ft.name}`}
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{ft.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{ft.code}</td>
                      <td className="px-6 py-4">
                        <Badge variant={ft.is_active ? 'success' : 'warning'}>
                          {ft.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          {canManage && (
                          <>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(ft)}>
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(ft.id)}>
                            <TrashIcon className="w-4 h-4 text-red-600" />
                          </Button>
                          </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal isOpen={showModal} onClose={handleCloseModal} title={editingId ? 'Edit Fuel Type' : 'Add Fuel Type'}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate(formData);
          }}
          className="space-y-4"
        >
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            required
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" isLoading={createMutation.isPending}>{editingId ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default FuelTypesPage;
